import {
  type SubjectIcon,
  type Tone,
  type ToneName,
  subjectLook,
  surfaces,
  tones,
} from '@shkola/design';
import {
  Atom,
  BookMarked,
  BookOpen,
  Dumbbell,
  Earth,
  FlaskConical,
  Hammer,
  HeartPulse,
  Landmark,
  Languages,
  Leaf,
  Library,
  type LucideIcon,
  Map as MapIcon,
  Monitor,
  Music,
  Palette,
  PenLine,
  Scale,
  Shield,
  Sigma,
  Sprout,
  Telescope,
  Triangle,
} from 'lucide-react';
import { type CSSProperties, type ReactNode, createContext, useContext } from 'react';
import { GRAD, SUBJECT_GRAD, grad } from '../student/home/ShapesArt';
import type { FilledIcon } from './icons';

/*
 * Design v4 (25.10): white blocks with a grey outline on a white panel, pastel tones for statuses
 * and subjects, line icons instead of stickers (stickers stay only on folders). Two ages: the
 * younger classes get the same blocks filled with their pastel tone, bigger and rounder.
 */

export type Age = 'junior' | 'teen';

/** НУШ: 1–4 — primary school, 5–6 — the adaptation cycle; from the 7th class the calmer look. */
export const JUNIOR_MAX_GRADE = 6;

const AgeContext = createContext<Age>('teen');
export const AgeProvider = AgeContext.Provider;
export const useAge = () => useContext(AgeContext);

const AGE_KEY = 'shkola.age';

/**
 * The look by the class; for showing the design, `?age=junior` / `?age=teen` overrides it in this
 * browser and `?age=auto` goes back to the class.
 */
export function ageOf(grade: number | null | undefined): Age {
  try {
    const asked = new URLSearchParams(window.location.search).get('age');
    if (asked === 'auto') localStorage.removeItem(AGE_KEY);
    else if (asked === 'junior' || asked === 'teen') localStorage.setItem(AGE_KEY, asked);
    const saved = localStorage.getItem(AGE_KEY);
    if (saved === 'junior' || saved === 'teen') return saved;
  } catch {
    // private mode: by the class
  }
  return grade != null && grade <= JUNIOR_MAX_GRADE ? 'junior' : 'teen';
}

/** The look chosen on the preview's switcher, kept in this browser like `?age=`. */
export function saveAge(age: Age) {
  try {
    localStorage.setItem(AGE_KEY, age);
  } catch {
    // private mode: only for this visit
  }
}

export const tone = (name: ToneName): Tone => tones[name];

const SUBJECT_ICONS: Record<SubjectIcon, LucideIcon> = {
  Dumbbell,
  Sigma,
  Triangle,
  Atom,
  Telescope,
  FlaskConical,
  Leaf,
  Sprout,
  Earth,
  Map: MapIcon,
  Landmark,
  Library,
  BookOpen,
  Languages,
  PenLine,
  Monitor,
  Palette,
  Music,
  Hammer,
  Shield,
  HeartPulse,
  Scale,
  BookMarked,
};

/** A card gradient of a tone (ref. «weihu»): more saturated at the top, lighter at the bottom. */
export const toneGradient = (name: ToneName, angle = 180): string =>
  `linear-gradient(${angle}deg, ${tones[name].from} 0%, ${tones[name].to} 100%)`;

/**
 * A block of the page: white with a thin grey outline, rows inside outlined too; for the younger
 * classes filled with its tone's gradient (ref. «weihu» cards), rows plain white.
 */
export function Block({
  tone: name = 'grey',
  className = '',
  children,
  style,
}: {
  tone?: ToneName;
  className?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  const age = useAge();
  if (age === 'junior') {
    return (
      <section
        className={`rounded-[32px] p-4 md:p-6 ${className}`}
        style={{ backgroundImage: toneGradient(name), ...style }}
      >
        {children}
      </section>
    );
  }
  return (
    <section className={`rounded-[28px] bg-white p-5 md:p-6 ${className}`} style={style}>
      {children}
    </section>
  );
}

/** The title row of a block: the name, a count in a grey circle, an action on the right. */
export function BlockHead({
  title,
  count,
  action,
  className = '',
}: {
  title: ReactNode;
  count?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const age = useAge();
  return (
    <div className={`mb-4 flex min-h-8 items-center gap-2 ${className}`}>
      <h2
        className={`font-display font-light tracking-[-0.03em] text-ink ${age === 'junior' ? 'text-[28px] leading-9' : 'text-[24px] leading-8'}`}
      >
        {title}
      </h2>
      {count !== undefined && <CountChip>{count}</CountChip>}
      {action && <div className="ml-auto flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  );
}

/** A number in a small grey circle next to a title (ref. «Recomended 28»). */
export function CountChip({ children }: { children: ReactNode }) {
  const age = useAge();
  return (
    <span
      className={`inline-flex h-6 min-w-6 items-center justify-center rounded-[7px] px-1.5 text-xs font-medium ${age === 'junior' ? 'bg-white/70 text-ink' : 'bg-[#F5F3EF] text-muted'}`}
    >
      {children}
    </span>
  );
}

/**
 * Pills sit on colour here: on a coloured surface a status is a white pill with coloured text (ref.
 * «Medium» on the peach card), on white or grey — a pastel pill.
 */
const OnColor = createContext(false);
export const OnColorProvider = OnColor.Provider;

/** A dot that breathes: something going on right now. */
export function LiveDot({ color, size = 6 }: { color: string; size?: number }) {
  return (
    <span className="relative flex shrink-0" style={{ width: size, height: size }}>
      <span
        className="absolute inline-flex size-full animate-ping rounded-full opacity-70"
        style={{ backgroundColor: color }}
      />
      <span className="relative size-full rounded-full" style={{ backgroundColor: color }} />
    </span>
  );
}

const CHIP_SIZE = {
  xs: { box: 'h-[22px] gap-1 rounded-[7px] px-1.5 text-[12px]', icon: 13 },
  sm: { box: 'h-6 gap-1 rounded-[8px] px-2 text-[12.5px]', icon: 14 },
  md: { box: 'h-8 gap-1.5 rounded-[10px] px-2.5 text-[14px]', icon: 16 },
} as const;

/**
 * A status chip (27.10, ref. «March 09 — 16», «Weekly», «Zoom»): a rounded square of the tone's pastel,
 * the bright text and a filled icon in the same colour, or a live dot. On a coloured surface — white
 * with the coloured text.
 */
export function StatusPill({
  tone: name,
  icon: Icon,
  dot = false,
  size = 'sm',
  children,
  className = '',
}: {
  tone: ToneName;
  icon?: FilledIcon;
  dot?: boolean;
  size?: keyof typeof CHIP_SIZE;
  children: ReactNode;
  className?: string;
}) {
  const g = SUBJECT_GRAD[name];
  const onColor = useContext(OnColor);
  const s = CHIP_SIZE[size];
  // «Форми 2»: a label is a soft rectangle in its tone's gradient, the words in the tone's ink
  return (
    <span
      className={`inline-flex shrink-0 items-center leading-none font-medium whitespace-nowrap ${s.box} ${className}`}
      style={
        onColor
          ? { backgroundColor: 'rgb(255 255 255 / 0.8)', color: GRAD[g].ink }
          : { backgroundImage: grad(g), color: GRAD[g].ink }
      }
    >
      {dot && <LiveDot color={GRAD[g].ink} />}
      {Icon && <Icon size={s.icon} weight="fill" aria-hidden className="shrink-0" />}
      {children}
    </span>
  );
}

/** A line icon on a pastel square (ref. the small app icons of a card). */
export function IconTile({
  tone: name,
  icon: Icon,
  size = 40,
  className = '',
}: {
  tone: ToneName;
  icon: LucideIcon;
  size?: number;
  className?: string;
}) {
  const age = useAge();
  const onColor = useContext(OnColor);
  // on a coloured surface the tile is a white circle, as a pill there is white
  const white = age === 'junior' || onColor;
  const g = SUBJECT_GRAD[name];
  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center ${white ? 'rounded-full' : 'rounded-[12px]'} ${className}`}
      style={{
        width: size,
        height: size,
        ...(white ? { backgroundColor: '#FFFFFF' } : { backgroundImage: grad(g) }),
        color: GRAD[g].ink,
      }}
    >
      <Icon size={Math.round(size * 0.48)} strokeWidth={1.8} />
    </span>
  );
}

/** A subject's line icon in its tone. */
export function SubjectTile({
  subject,
  size = 40,
  className,
}: {
  subject: string;
  size?: number;
  className?: string;
}) {
  const look = subjectLook(subject);
  return (
    <IconTile tone={look.tone} icon={SUBJECT_ICONS[look.icon]} size={size} className={className} />
  );
}

/** The two kinds of buttons: near-black navy for the main action (ref. «Request Seats»), white for the rest. */
const BUTTON_SIZE = {
  sm: 'h-8 px-3.5 text-[13px]',
  md: 'h-10 px-4.5 text-sm',
  lg: 'h-12 px-6 text-[15px] gap-2',
} as const;

export function buttonClass(
  kind: 'primary' | 'outline',
  size: keyof typeof BUTTON_SIZE = 'md',
): string {
  const base = `inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full font-medium whitespace-nowrap transition-colors duration-200 ${BUTTON_SIZE[size]}`;
  return kind === 'primary'
    ? `${base} bg-primary text-white hover:bg-primary-hover`
    : `${base} bg-white text-ink ring-1 ring-[#E4E0D7] ring-inset hover:bg-[#FAF9F6]`;
}

/** A round icon button with an outline (ref. ‹ › and the bell). */
export const iconButtonClass =
  'relative inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-white text-ink-soft ring-1 ring-stroke ring-inset transition-colors hover:bg-soft hover:text-ink';

/** A row of thin ticks: how much of something is done (ref. «12/32» under a stage). */
export function TickProgress({
  value,
  total,
  tone: name = 'orange',
  ticks = 24,
}: {
  value: number;
  total: number;
  tone?: ToneName;
  ticks?: number;
}) {
  const count = Math.max(1, Math.min(ticks, total));
  const filled = total > 0 ? Math.round((value / total) * count) : 0;
  return (
    <span className="flex items-center gap-[3px]" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          className="block h-4 w-[5px] rounded-[2px]"
          style={{ backgroundColor: i < filled ? tones[name].icon : surfaces.stroke }}
        />
      ))}
    </span>
  );
}

/** A key of the keyboard, as in «⌘K». */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 items-center rounded-md border border-stroke bg-white px-1.5 font-sans text-[11px] font-medium text-muted">
      {children}
    </kbd>
  );
}

/** The first letters of a name on a pastel circle: no animals and no photos. */
export function Initials({
  name,
  size = 40,
  tone: name2 = 'violet',
}: {
  name: string;
  size?: number;
  tone?: ToneName;
}) {
  const letters = name
    .split(/[\s-]+/)
    .filter((w) => /^[\p{L}\p{N}]/u.test(w))
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
  const t = tones[name2];
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-full font-display font-semibold"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.36),
        backgroundColor: t.fill,
        color: t.ink,
      }}
    >
      {letters || '•'}
    </span>
  );
}
